import { oauthAuthorizeUrl } from '../lib/apiClient';

/**
 * "Sign in with Google" button — follows Google's brand guidelines:
 *   - White background, 1px grey border
 *   - Multicolor "G" mark on the left
 *   - "Sign in with Google" text in Roboto-ish system stack
 *
 * On click, the browser navigates to the Gateway's OAuth start URL
 * with `redirect_uri = <window.location.href without query/hash>`.
 * After the round-trip, the Gateway redirects back here with the
 * tokens in the URL fragment — `consumeOAuthFragment()` picks them up
 * on mount.
 *
 * Pass `redirectUri` to override the default (e.g. on InviteLanding
 * we want to come back to /invite/:token, NOT /login).
 */
export function GoogleSignInButton({
  label = 'Sign in with Google',
  redirectUri,
  disabled = false,
  className = '',
}: {
  label?: string;
  redirectUri?: string;
  disabled?: boolean;
  className?: string;
}) {
  function onClick() {
    const target =
      redirectUri ??
      window.location.origin + window.location.pathname; // strip ?query and #hash
    window.location.assign(oauthAuthorizeUrl('google', target));
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`btn d-flex align-items-center justify-content-center gap-2 ${className}`}
      style={{
        width: '100%',
        backgroundColor: '#ffffff',
        color: '#3c4043',
        border: '1px solid #dadce0',
        fontWeight: 500,
        fontFamily:
          "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        height: 40,
        boxShadow: 'none',
      }}
    >
      <GoogleGMark />
      <span>{label}</span>
    </button>
  );
}

/**
 * Google's official multicolor "G" mark — re-drawn from the public
 * brand guidelines (the "G" in the Google logo). Inline SVG so we
 * don't depend on hot-linking Google's hosted asset (which their
 * own brand guidelines say to avoid).
 */
function GoogleGMark() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
